// @ts-nocheck
/**
 * Agent Announcements list — redesigned to obd3-gui pattern (FillPage +
 * PageHeader + DataTable + DropdownMenu row actions + ConfirmDialog).
 * Preserved: multi-delete, duplicate-heading warning highlight, ACL gates.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENT_ANNOUNCEMENT_LIST,
  API_AGENT_ANNOUNCEMENT_DELETE,
} from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

const AgentAnnouncementList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [dataList, setDataList] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings)
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchData();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchData = () => {
    getApiCall(API_AGENT_ANNOUNCEMENT_LIST)
      .then((resp: any) => {
        const updated = (resp || []).map((val: any, idx: number) => ({
          ...val,
          key: idx + 1,
        }));
        setDataList(updated);
        setOriginalData(updated);
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Failed to fetch announcements');
        console.log('Error fetching announcements:', err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();
      setDataList(
        originalData.filter(
          (item: any) =>
            item.heading?.toLowerCase().includes(q) ||
            item.content?.toLowerCase().includes(q),
        ),
      );
    } else {
      setDataList(originalData);
    }
  }, [searchValue, originalData]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (pendingBulk) {
        await Promise.all(
          selectedItems.map((id: any) =>
            postApiCall(API_AGENT_ANNOUNCEMENT_DELETE, { id }),
          ),
        );
        toast.success('Selected announcements deleted successfully');
        setSelectedItems([]);
        setPendingBulk(false);
      } else if (pendingDelete) {
        await postApiCall(API_AGENT_ANNOUNCEMENT_DELETE, { id: pendingDelete.id });
        toast.success('Announcement deleted successfully');
        setPendingDelete(null);
      }
      fetchData();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to delete announcement');
    } finally {
      setDeleting(false);
    }
  };

  const isDuplicateHeading = (data: any) =>
    dataList.filter(
      (item: any) =>
        item.heading?.toLowerCase().trim() === data.heading?.toLowerCase().trim(),
    ).length > 1;

  const canEdit = isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.edit;
  const canDelete = isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.delete;
  const canAdd = isSuperAdmin || loggedInUserACL[MODULE_ACL.AGENT_ANNOUNCEMENT]?.add;
  const showActions = canEdit || canDelete;

  const handleSelectAll = (checked: any) => {
    setSelectedItems(checked ? dataList.map((item: any) => item.id) : []);
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: '_select',
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: () => (
          <Checkbox
            checked={selectedItems.length === dataList.length && dataList.length > 0}
            onCheckedChange={handleSelectAll}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedItems.includes(row.original.id)}
            onCheckedChange={() =>
              setSelectedItems((prev: any[]) =>
                prev.includes(row.original.id)
                  ? prev.filter((i: any) => i !== row.original.id)
                  : [...prev, row.original.id],
              )
            }
            aria-label="Select row"
          />
        ),
      },
      {
        accessorKey: 'heading',
        header: 'Heading',
        cell: ({ row }) => {
          const dup = isDuplicateHeading(row.original);
          const el = canEdit ? (
            <Link
              to={`/agent-announcements/edit/${row.original.id}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.heading}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{row.original.heading}</span>
          );
          return dup ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-0.5"
              title="Duplicate heading — another row has the same heading"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              {el}
            </span>
          ) : (
            el
          );
        },
      },
      {
        accessorKey: 'content',
        header: 'Content',
        cell: ({ row }) => (
          <span className="line-clamp-2 text-xs text-muted-foreground">
            {row.original.content || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const raw = row.original.status;
          const active = raw === undefined || raw === null ? true : Boolean(raw);
          return (
            <span
              className={
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                (active
                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'bg-muted/60 text-muted-foreground ring-border/60')
              }
            >
              {active ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.updated_at)}
          </span>
        ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/agent-announcements/edit/${row.original.id}`);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="Edit"
                aria-label="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {canDelete && selectedItems.length === 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(row.original);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                title="Delete"
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [selectedItems, dataList, canEdit, canDelete, showActions, navigate]);

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Agent Announcements"
        description="Broadcast messages shown to agents inside the CTI panel."
        actions={
          <>
            {canDelete && selectedItems.length > 0 && (
              <Button
                variant="destructive"
                onClick={() => setPendingBulk(true)}
                disabled={selectedItems.length === 0}
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedItems.length})
              </Button>
            )}
            {canAdd && (
              <Button asChild>
                <Link to={`/${ROUTES.AGENT_ANNOUNCEMENT.CREATE}`}>
                  <Plus className="h-4 w-4" />
                  Create Announcement
                </Link>
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="agent-announcements:list:v1"
        columns={columns}
        data={dataList}
        isLoading={loading}
        searchPlaceholder="Search by heading or content…"
        emptyTitle="No announcements yet"
        emptyDescription="Create an announcement to broadcast a message to your agents."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected announcements?' : 'Delete this announcement?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">{selectedItems.length}</span>{' '}
              announcement(s) will be permanently deleted.
            </>
          ) : (
            <>
              Announcement{' '}
              <span className="font-medium text-foreground">
                {pendingDelete?.heading || `#${pendingDelete?.id}`}
              </span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default AgentAnnouncementList;
